/**
 * usePaperSheet — 시험지 문항을 **실제 지면(현행 인쇄 v4)** 으로 세우는 계산 한 벌.
 *
 * 시험지 조립 창(PaperComposeModal)의 우측 지면에서 떼어 냈다(오너 2026-09-14 — 학생 화면 「시험지 불러오기」 창에도
 * 같은 지면 서랍을 붙이려고). 두 창이 같은 지면을 보여 주도록 계산은 여기 한 곳에만 둔다. 얼굴(배율 단추·[미리보기]·
 * 스크롤 칸·손잡이)은 PaperSheetView 가, 서랍을 어디에 어떻게 세울지는 부르는 창이 갖는다.
 *
 * 지면은 **현행 인쇄 v4 그대로**다 — 규격·측정·배치·조판을 여기서 다시 만들지 않고 src/print/ 의 부품을 불러 쓴다.
 * 정적으로 들이는 것이 무겁지 않은 근거: App.js 가 ProblemPrint 를 정적으로 들이고 그쪽이 printPreview.js 를 통해
 * katex 와 PrintOverlayV4 를 끌어와, 이 모듈들은 **이미 본 번들에 있다**.
 *
 * 입력
 *   active      — 지면을 세울 때만 참(창이 열려 있고, 불러오기 창은 서랍을 한 번이라도 폈을 때). 거짓이면 본문을 묻지 않고
 *                 측정·맞춤도 멈춘다. 거짓이 되면 전체화면과 손 배율을 내린다 — 다음에 열면 맞춤에서 시작한다.
 *   problemIds  — 지면 순서 그대로의 문제 id(타입 그대로 — 교체 대상 비교가 === 다)
 *   title       — 머리글 제목(0.4초 묵혀 넣는다 — 한 글자마다 지면을 다시 조판하지 않게)
 *   examLabel   — 머리글의 시험 번호 칸(조립 창은 저장 전이면 「미저장」)
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { fetchWithTokenRefresh } from '../../utils/apiUtils';
import { loadLexicalToHtml } from './lexicalHtml';
import { PX_PER_MM, getSheetGeometry } from '../../print/printSpec';
import { measureProblemHeightsMm, distributeProblemsV4 } from '../../print/measureV4';
import { makePatternOf, isAllAuto } from '../../print/pagePatterns';
import { useAuth } from '../../context/AuthContext';
import { createPrintDraftContext, loadScopedPrintPrefs, normalizePrintContext, printDraftScope } from '../../print/printDrafts';
import { b4MarkersForSheet } from '../../print/b4Markers';
// 정답 왕복과 이미지 대기는 인쇄 오버레이 안의 지역 함수였다 — 사본을 늘리지 않으려고
// 그쪽에 export 두 단어만 붙여 그대로 부른다.
import { useAnswers, preloadContentImages } from '../../print/PrintOverlayV4';

const CONTENT_BATCH = 20; // print/page 한 번에 물어보는 문항 수(PresetModal 과 같은 단위)

// 지면 폭의 어림값(PAGE_PX_GUESS = 1240 = 328mm)은 **없어졌다.** v2 의 .page 는 A4 두 장을
// 가로로 붙인 판이라 어림이 필요했고, 그 값을 그대로 믿었을 때 40% 가 나왔는데 실제로 들어가는
// 값은 35% 였다(오너 실측 2026-08-26 — v2 때의 수치다). v4 의 지면 폭은 곧 용지 실치수라
// (.pv4-page 의 width) 어림이 아니라 계산으로 정확히 안다 → geo.paper.w × PX_PER_MM.
// 실측 보정(naturalRef)은 CSS zoom 의 1px 반올림 때문에 그대로 남는다.
// 세로 스크롤바가 앉을 자리. 지금 붙어 있으면 그 폭을 재서 쓰고, 아직 없을 때만 이 값으로
// 예약한다 — 지면은 A4 두 장을 세로로 이은 길이(464mm)라 이 열 높이에 들어가는 경우가 없고,
// 그려진 뒤에 스크롤바가 생기면 그만큼 가로가 넘친다.
const SCROLLBAR_PX_GUESS = 16;
const FIT_MIN = 0.12;   // 맞춤이 내려갈 수 있는 바닥
const FIT_TRIES = 4;    // 한 폭에서 줄여 보는 횟수 — 재고 줄이기를 무한히 반복하지 않는다
// 서랍 폭 = 지면 실치수(배율 100%) + 칸 안쪽 여백 p-3 양쪽 24 + 세로 스크롤바 예약 16 + 반올림 여유 2.
// 이 폭이면 「맞춤」이 100% 로 떨어진다(오너 2026-09-14 「지면을 접을 때 폭을 너무 크게」 — 86% 서랍은 1440 에서 143% 로 부풀었다)
const DRAWER_EXTRA_PX = 42;

/**
 * 지면 칸의 실제 치수. sbFallback 은 세로 스크롤바가 지금 없을 때 예약할 폭이다.
 * sb 로 지금 붙어 있는 스크롤바의 실측 폭을 돌려준다(다음 계산의 sbFallback 이 된다).
 */
const measureBox = (el, sbFallback) => {
  const cs = window.getComputedStyle(el);
  const padL = parseFloat(cs.paddingLeft) || 0;
  const padR = parseFloat(cs.paddingRight) || 0;
  const borderX = (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0);
  // 세로 스크롤바가 이미 있으면 clientWidth 에서 빠져 있다 — 두 번 빼지 않는다
  const sb = el.offsetWidth - borderX - el.clientWidth;
  const hasVBar = sb > 0.5;
  return {
    padL,
    padR,
    sb: hasVBar ? sb : 0,
    inner: el.clientWidth - padL - padR - (hasVBar ? 0 : (sbFallback || SCROLLBAR_PX_GUESS)),
    // 가로로 넘친 양. scrollWidth 를 그대로 쓰면 안 된다 — 넘치지 않을 때는 padding-right 를
    // 포함하고 넘칠 때는 빠뜨리는 비대칭이 있어, 딱 맞는 상태가 '오른쪽 여백만큼 넘쳤다'로
    // 읽힌다. 차이로 재면 그 여백이 양쪽에서 상쇄돼 들어갔을 때 정확히 0 이 된다.
    overflowX: Math.max(0, el.scrollWidth - el.clientWidth),
  };
};
const LOADING_HTML = '<p style="color:#9ca3af;font-size:18px">본문을 불러오는 중…</p>';
const FAILED_HTML = '<p style="color:#dc2626;font-size:18px">본문을 불러오지 못했습니다.</p>';
// 측정 전 — PrintSheetV4 는 배열 **참조**로 memo 를 가른다. 매 렌더 새 배열을 넘기면 그것만으로 다시 그린다
export const NO_PAGES = [];

export default function usePaperSheet({
  active, problemIds, title = '', examLabel = '', getAccessToken, printContext = null,
}) {
  const { userId } = useAuth() || {};
  const [temporaryContext] = useState(createPrintDraftContext);
  const contextKind = printContext?.kind;
  const contextId = printContext?.id;
  const context = useMemo(() => normalizePrintContext({ kind: contextKind, id: contextId }) || temporaryContext,
    [contextKind, contextId, temporaryContext]);
  const scope = printDraftScope(userId, context);
  // 본문 캐시는 problem_id → HTML 이다. id 로만 결정되는 값이라 순서를 바꾸거나 문항을 빼도
  // 다시 물어볼 이유가 없다 — 담기·교체로 처음 보는 id 가 생겼을 때만 요청이 나간다.
  // 창을 닫아도 지우지 않는다 — 같은 문제를 다시 담을 때마다 지면이 '불러오는 중'으로 되돌아가지 않게.
  const [contentMap, setContentMap] = useState({});
  const contentPending = useRef(new Set()); // 지금 물어보는 중인 id — 같은 id 를 두 번 묻지 않는다
  const [previewIds, setPreviewIds] = useState([]); // 순서가 진짜로 바뀔 때만 갈리는 id 배열
  const [previewKey, setPreviewKey] = useState(''); // 그 배열이 언제 갈려야 하는지 재는 자

  const [previewTitle, setPreviewTitle] = useState('');
  const [fitScale, setFitScale] = useState(0.4);
  const [zoom, setZoom] = useState(null); // null 이면 열 폭에 맞춘다
  const previewBoxRef = useRef(null);
  // 지면 칸 요소 — 붙는 순간을 state 로도 받는다(boxRef). 창(Modal)이 알맹이를 늦게 붙이면 ResizeObserver 를 거는 effect 가
  // 먼저 돌아 칸이 없어 그냥 끝나고, 그 뒤로는 의존성이 안 바뀌어 영영 걸리지 않았다 — 처음 배율이 0.4 에 머물고 칸 폭이
  // 바뀌어도 다시 맞추지 않았다(2026-09-14 실측: 조립 창 서랍 40%, 강의 칸을 접어 열로 바꾸자 가로 670px 넘침)
  const [boxEl, setBoxEl] = useState(null);
  const boxRef = useCallback((el) => { previewBoxRef.current = el; setBoxEl(el); }, []);
  const naturalRef = useRef(null);  // 배율 1 일 때 지면이 차지하는 가로 폭 — 넘쳤을 때 실측해 둔다
  const sbRef = useRef(0);          // 세로 스크롤바 실측 폭 — 사라진 동안의 자리 예약에 쓴다
  const fitTriesRef = useRef(0);    // 지금 폭에서 줄여 본 횟수
  const scaleRef = useRef(0.4);     // 재는 함수가 최신 배율을 읽는 통로(effect 의존성을 늘리지 않으려고)

  // 전체화면 [미리보기] — 인쇄·PDF 는 여전히 그쪽 몫이고 인라인 지면은 축소판이다
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // ── 지면 규격 ──
  // 용지(A4/B4)·B4 머리글·면 배열 프리셋은 **전체화면 [미리보기]에서 고른 값**을 그대로 읽는다.
  // 용지·배율은 공통 설정, B4 문구·마커·기본 패턴은 계정·시험지별 임시 설정을 읽는다.
  // 고르는 자리를 여기 따로 만들지 않는 이유가 그것이다 — 두 곳이 되면 「화면이 보여준 용지」와
  // 「실제로 인쇄되는 용지」가 갈린다. 그 값이 곧 @page{size} 를 정하는 값이다(usePrintV4).
  const settingsIdsKey = JSON.stringify(previewIds.map(String));
  const [prefsState, setPrefsState] = useState(() => ({ scope, settingsIdsKey,
    prefs: loadScopedPrintPrefs(userId, context, previewIds) }));
  const printPrefs = prefsState.scope === scope && prefsState.settingsIdsKey === settingsIdsKey
    ? prefsState.prefs : loadScopedPrintPrefs(userId, context, previewIds);
  const paperKey = printPrefs.paperKey;
  const geo = useMemo(() => getSheetGeometry(paperKey), [paperKey]);
  // 측정 결과는 **잰 그 문항·그 용지와 한 덩어리로** 들고 있다. 따로 두면 새 본문이 옛 높이 위에
  // 그려져 문항이 포개진다 — v4 문항은 absolute 배치라 높이가 어긋나면 잘리는 게 아니라 겹친다.
  // 한 덩어리면 재측정 중에도 직전 지면이 온전히 남아 백지 깜빡임도 없다.
  const [laid, setLaid] = useState(null);

  // 지면을 내리면 전체화면과 손 배율도 내린다 — 다음에 열면 맞춤에서 시작한다
  useEffect(() => {
    if (active) return;
    setPreviewOpen(false);
    setPreviewData(null);
    setZoom(null);
  }, [active]);

  // ── 지면 본문 읽기 ─────────────────────────────────────────────────────────
  // 겹침은 순번이 아니라 contentPending 으로 막는다: 한 id 는 동시에 한 번만 요청되고, 결과는
  // id 를 키로 합쳐 넣으므로 늦게 온 응답이 먼저 온 것을 덮을 수가 없다. 순번으로 늦은 판을
  // 버리면 그 id 들은 pending 에서도 빠져 영영 빈칸으로 남는다.
  const fetchContents = useCallback(async (ids) => {
    ids.forEach((id) => contentPending.current.add(id));
    try {
      const toHtml = await loadLexicalToHtml();
      for (let i = 0; i < ids.length; i += CONTENT_BATCH) {
        const chunk = ids.slice(i, i + CONTENT_BATCH);
        // 응답에 빠진 id 까지 미리 채운다 — 안 채우면 그 문항이 '불러오는 중'에 갇힌다
        const next = {};
        chunk.forEach((id) => { next[id] = { content: FAILED_HTML, ok: false }; });
        try {
          const res = await fetchWithTokenRefresh(
            `/api/problems/print/page?problem_ids=${chunk.join(',')}&page=1&per_page=${CONTENT_BATCH}`,
            {},
            getAccessToken,
          );
          const list = Array.isArray(res?.data) ? res.data : [];
          list.forEach((item) => {
            const key = String(item.problem_id);
            if (!next[key]) return; // 물어보지 않은 id 는 캐시에 넣지 않는다
            try {
              const doc = item.lexical_data?.lexical_data;
              // 한 문항의 변환 실패가 같은 배치의 멀쩡한 문항까지 삼키지 않게 문항별로 가둔다
              if (doc) next[key] = { content: toHtml(doc), ok: true };
            } catch (convErr) {
              console.error('문제 본문 변환 실패:', key, convErr);
            }
          });
        } catch (e) {
          console.error('문제 본문 로드 실패:', e);
        }
        // 배치마다 합쳐 넣는다 — 앞쪽 문항부터 지면이 차례로 채워진다
        setContentMap((prev) => ({ ...prev, ...next }));
      }
    } catch (e) {
      console.error('본문 변환기 로드 실패:', e);
      const next = {};
      ids.forEach((id) => { next[id] = { content: FAILED_HTML, ok: false }; });
      setContentMap((prev) => ({ ...prev, ...next }));
    } finally {
      ids.forEach((id) => contentPending.current.delete(id));
    }
  }, [getAccessToken]);

  // 아직 본문이 없는 id 만 모아 한 번에 묻는다. 문자열로 재 두는 이유는 행을 손댈 때마다
  // 부르는 쪽 배열이 새로 생기기 때문 — 값이 같으면 이 effect 는 돌지 않아야 한다.
  // problem_id 가 없는 행은 본문을 물어볼 수도 없다 — 요청에 'undefined' 를 실어 보내지 않는다
  const askableIds = problemIds.map((id) => (id == null ? '' : String(id))).filter(Boolean);
  const missingKey = askableIds
    .filter((id) => !contentMap[id] && !contentPending.current.has(id))
    .join(',');
  useEffect(() => {
    if (!active || missingKey === '') return;
    // StrictMode 의 두 번째 호출까지 막으려면 pending 은 effect 안에서 다시 걸러야 한다
    const ids = missingKey.split(',').filter((id) => !contentPending.current.has(id));
    if (ids.length > 0) fetchContents(ids);
  }, [active, missingKey, fetchContents]);

  const missingCount = askableIds.filter((id) => !contentMap[id]).length;
  const failedCount = askableIds.filter((id) => contentMap[id]?.ok === false).length;
  /** 못 읽은 본문만 캐시에서 지운다 — 위 effect 가 그 id 들을 다시 묻는다 */
  const retryContents = () => {
    setContentMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => { if (next[id]?.ok === false) delete next[id]; });
      return next;
    });
  };

  // 지면에 넘길 id 배열 — 순서가 진짜로 바뀔 때만 갈린다. 배지·미리보기가 새로 붙을 때마다 부르는 쪽 배열은
  // 새 배열이 되지만 구성은 그대로이고, 그 참조가 흔들리면 지면이 문항 수만큼 리플로우를 다시 돈다.
  // effect 가 아니라 렌더 중에 맞추는 이유: effect 로 미루면 문항을 처음 담은 프레임에 빈 지면이
  // 한 번 스친다(React 의 "props 로 state 를 맞추기" 패턴 — 커밋 없이 즉시 다시 렌더된다).
  const idKey = problemIds.join(',');
  if (idKey !== previewKey) {
    setPreviewKey(idKey);
    setPreviewIds(problemIds);
  }

  const previewProblems = useMemo(
    () => previewIds.map((id) => {
      const hit = contentMap[String(id)];
      return {
        problem_id: id, // 부르는 쪽의 값을 그대로 넘긴다 — 교체 대상 비교는 === 라 타입이 갈리면 안 된다
        content: hit ? (hit.content || '') : LOADING_HTML, // 본문이 정말 빈 문항을 영영 '불러오는 중'에 두지 않는다
        // answer 를 넣지 않는 것이 곧 '정답 없음'이 아니다 — 정답은 아래 useAnswers 가 따로
        // 채운다(문항에 answer 가 하나도 없으면 /api/solutions/answers 를 친다. 인쇄가 쓰는
        // 그 훅을 그대로 부른다). 여기서 answer:'' 를 넣으면 요청은 사라지지만 정답지가
        // 전부 '-' 가 된다. 왕복이 배치마다 반복되지 않게 하는 것은 id 만 담은 배열이 맡는다.
      };
    }),
    [previewIds, contentMap],
  );

  // ── 측정 · 배치 · 정답 · 머리글 ───────────────────────────────────────────────
  // 면 배열은 프리셋 키(문자열)에만 매인다 — printPrefs 를 다시 읽어 객체가 새로 생겨도
  // 이 둘은 그대로라 재측정이 나지 않는다. 면별 지정(patOverrides)은 오버레이가 일부러
  // 저장하지 않으므로 인라인은 프리셋까지만 따라간다.
  const presetKey = printPrefs.patPreset[paperKey];
  const patternOf = useMemo(() => makePatternOf(presetKey), [presetKey]);
  const allAuto = isAllAuto(presetKey);

  // 전체화면에서 용지·머리글을 고치고 닫으면 그 값을 여기로 데려온다.
  // 같은 탭에서는 storage 이벤트가 뜨지 않아 읽는 시점을 우리가 정한다 — 지면을 세울 때와
  // 전체화면이 닫힐 때 읽는다. 시험지·계정·문항 순서가 바뀌어도 다시 읽어 마커 번호를 맞춘다.
  useEffect(() => {
    if (active && !previewOpen) setPrefsState({ scope, settingsIdsKey,
      prefs: loadScopedPrintPrefs(userId, context, JSON.parse(settingsIdsKey)) });
  }, [active, previewOpen, scope, settingsIdsKey, userId, context]);

  useEffect(() => { setPreviewOpen(false); setPreviewData(null); }, [scope]);

  // 문항 높이 실측 — 인쇄와 **같은 함수**를 그대로 부른다.
  // 결과를 { geo, problems, heights, patternOf, allAuto } 한 덩어리로 갈아끼우므로,
  // 지면은 언제나 「잰 그 문항 · 그 용지 · 그 배열」만 그린다.
  useEffect(() => {
    if (!active || previewProblems.length === 0) { setLaid(null); return undefined; }
    let alive = true;
    (async () => {
      try {
        if (document.fonts?.ready) await document.fonts.ready;   // 오버레이와 같은 순서
        // 〃 — 다만 4초 상한 뒤에도 안 온 그림이 있으면 그 그림들을 붙들고 기다릴 late 를 받는다
        const pre = await preloadContentImages(previewProblems.map((p) => p.content || ''), { trackLate: true });
        if (!alive) return;
        // await 뒤에도 effect 에 들어올 때 잡은 previewProblems·geo 만 쓴다 — 다시 읽지 않는다
        const snap = (heights) => setLaid({ geo, problems: previewProblems, heights, patternOf, allAuto });
        // 한 번 재고, 측정 직후 글꼴 상태가 'loading' 이면 도착을 기다려 한 번 더 잰다.
        // 인쇄 서체의 @font-face 는 PrintSheetV4.sheetChromeCss 에만 있다 — 서체 이름을 여기 적지 않는 이유:
        // 목록의 단일 출처는 sheetChromeCss 다. 첫 값을 먼저 그려 두므로 기다리는 동안 화면이 비지 않는다.
        const measurePass = async () => {
          snap(measureProblemHeightsMm(previewProblems, geo));
          if (document.fonts && document.fonts.status === 'loading') {
            await document.fonts.ready;
            if (!alive) return;
            snap(measureProblemHeightsMm(previewProblems, geo));
          }
        };
        await measurePass();
        if (!alive) return;
        // 4초 안에 못 온 그림이 있을 때만 — 다 온 뒤 한 번 더 잰다.
        // 지금까지는 측정이 문항마다 <style> 을 넣었다 빼서, 측정 직후 글꼴 상태가 (A4 에서도) 'loading' 으로
        // 남아 위의 두 번째 측정이 우연히 돌았고, 그 우연이 4초 상한 뒤 도착한 그림을 잡아 주고 있었다.
        // 묶음 측정(measureV4)에서는 그 우연이 사라지므로 늦은 그림을 명시적으로 기다린다.
        // 흉내(jest.fn)가 undefined 를 돌려줘도 넘어가도록 모양을 확인한다.
        const late = pre?.late;
        if (late && typeof late.then === 'function') {
          await late;
          if (!alive) return;
          await measurePass();
        }
      } catch (e) {
        // 폰트·이미지 대기나 측정이 실패해도 창은 산다 — 직전 지면이 그대로 남는다
        console.error('지면 측정 실패:', e);
      }
    })();
    return () => { alive = false; };
  }, [active, previewProblems, geo, patternOf, allAuto]);

  // 배치도 인쇄와 같은 함수·같은 인자다. B4 기본 프리셋이 'suneung' 이라 allAuto 분기가 없으면
  // 인라인만 그리디로 떨어져 면당 문항 수가 인쇄와 달라진다.
  const problemPages = useMemo(() => {
    if (laid == null) return NO_PAGES;
    const opts = laid.allAuto
      ? { heights: laid.heights }
      : { heights: laid.heights, patternOf: laid.patternOf };
    return distributeProblemsV4(laid.problems, laid.geo, opts);
  }, [laid]);

  // 빠른정답 — 인쇄와 **같은 훅**이 만든다. useAnswers 의 의존성은 배열 **참조**라, 본문이
  // 20개씩 도착할 때마다 새로 생기는 previewProblems 를 그대로 넘기면 왕복이 배치마다 반복된다.
  // id 만 담은 배열은 순서가 진짜로 바뀔 때만 갈린다. problem_id 는 **타입 그대로** 옮긴다.
  const answerProblems = useMemo(() => previewIds.map((id) => ({ problem_id: id })), [previewIds]);
  const { answers } = useAnswers(answerProblems, active && previewIds.length > 0);

  // 지면 머리글. 앞 넷은 조립 창이 넘기던 값 그대로이고, b4* 는 인쇄가 읽는 그 값이다(printPrefs)
  // — 미리보기가 인쇄와 다른 얼굴을 보이지 않게.
  const sheetMeta = useMemo(() => {
    return {
      tree_name: previewTitle || '제목 없는 시험지',
      edu_ver: '시험지',
      student_name: '',   // 시험지에는 학생 이름이 없다 — A4 는 빈 밑줄만 남는다(오너 수용)
      exam_id: examLabel,
      b4Line1: printPrefs.b4Line1,
      b4Line2: printPrefs.b4Line2,
      b4Title: printPrefs.b4Title,
      b4Subject: printPrefs.b4Subject,
      // 유형 마커도 넘긴다 — 클릭 상자가 문항 자신이라 좌표를 다시 조립하지 않으므로,
      // 마커가 붙은 문항의 10.5mm 시프트가 클릭을 어긋나게 하지 않는다.
      b4Markers: b4MarkersForSheet(printPrefs, previewIds.length),
    };
  }, [previewTitle, examLabel, previewIds.length, printPrefs]);

  // 제목은 지면 머리글로 들어간다 — 한 글자마다 지면을 다시 조판하지 않게 잠깐 묵힌다
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setPreviewTitle(String(title || '').trim()), 400);
    return () => clearTimeout(t);
  }, [active, title]);

  // 배율은 transform 이 아니라 CSS zoom 으로 들어가 레이아웃 박스가 실제로 줄어든다 —
  // 부모의 overflow-auto 가 그래서 정상 동작하고, 우리가 '넘쳤는지'를 잴 수 있는 것도 이 덕이다.
  const scale = zoom ?? fitScale;
  scaleRef.current = scale; // 아래 재는 함수들이 의존성 없이 최신 배율을 읽는다

  /** 지면 칸을 잰다. 세로 스크롤바가 붙어 있는 김에 그 폭도 기억해 둔다. */
  const readBox = useCallback(() => {
    const el = previewBoxRef.current;
    if (el == null) return null;
    const m = measureBox(el, sbRef.current);
    if (m.sb > 0) sbRef.current = m.sb; // 실측한 스크롤바 폭 — 사라진 뒤의 예약에 쓴다
    return m.inner > 0 ? m : null; // 좁은 폭에서 숨은 열은 0 이다 — 직전 값을 유지한다
  }, []);

  /** 열 폭에 맞는 배율 — 아는 지면 폭으로 나눈다. */
  const estimateFit = useCallback(() => {
    const m = readBox();
    if (m == null) return null;
    // 실측값은 **잰 그 용지에서만** 쓴다 — A4 로 잰 폭으로 B4 를 나누면 배율이 과대평가돼
    // 가로 스크롤이 남는다. 없거나 다른 용지면 규격에서 받는다(A4 793.7 · B4 971.3px).
    const nat = naturalRef.current;
    const natural = (nat != null && nat.paper === paperKey) ? nat.px : geo.paper.w * PX_PER_MM;
    // 올림하면 지면이 열보다 넓어져 가로 스크롤이 난다 — 1% 단위로 내림한다.
    // **위쪽 캡이 없다.** 「맞춤」의 정의가 가로 스크롤이 나지 않는 가장 큰 배율이기 때문이고,
    // v2 지면(328mm)에서는 어떤 칸도 그보다 좁아 100% 에 닿을 일이 없었다. v4 는 넓은 칸에서
    // 넘어선다 — 실제 상한은 칸이 정한다. 캡을 두면 그만큼 좌우가 빈다(오너: 「현재처럼 화면에 맞게」).
    return Math.max(FIT_MIN, Math.floor((m.inner / natural) * 100) / 100);
  }, [readBox, geo, paperKey]);

  /** 그려진 뒤에 실제로 넘쳤는지 보고, 넘쳤으면 그만큼 내린다.
      계산으로 미리 맞히지 못하는 이유가 둘이다: ① 지면이 차지하는 폭은 .page 의 328mm 가
      아니다(그 밖으로 나가는 것이 있다) ② CSS zoom 은 mm 를 픽셀로 바꾼 뒤 배율을 먹여
      1px 안팎의 반올림이 남는다. 그래서 넘친 김에 '배율 1 일 때의 폭'을 역산해 적어 두고,
      다음부터는 그 실측값으로 한 번에 맞춘다. */
  const shrinkIfOverflow = useCallback(() => {
    const m = readBox();
    if (m == null || m.overflowX <= 0) return; // 들어갔다 — 아무것도 건드리지 않는다
    const cur = scaleRef.current;
    if (cur <= FIT_MIN || fitTriesRef.current >= FIT_TRIES) return;
    fitTriesRef.current += 1;
    // 넘칠 때 scrollWidth 가 빠뜨리는 오른쪽 여백을 되돌려, 지금 차지한 실제 폭을 만든다
    const used = m.inner + m.overflowX + m.padR;
    // 배율 1 로 되돌린 폭 = 지면의 실제 폭. **잰 용지를 함께 적는다** — 안 적으면 용지를
    // 바꾼 뒤에도 옛 용지의 폭으로 나누게 된다(리셋 effect 를 따로 두는 길은 선언 순서에
    // 기대는 함정이 있다: ResizeObserver 가 먼저 서면 옛 폭으로 굳고 아무도 다시 안 잰다).
    naturalRef.current = { paper: paperKey, px: used / cur };
    const next = Math.max(FIT_MIN, Math.floor((m.inner / naturalRef.current.px) * 100) / 100);
    // 계산이 제자리면(반올림 때문에) 1% 는 강제로 내린다 — 안 그러면 넘친 채로 멈춘다
    setFitScale((prev) => (next < prev ? next : Math.max(FIT_MIN, Math.round((prev - 0.01) * 100) / 100)));
  }, [readBox, paperKey]);

  // 칸 폭이 바뀌면 다시 맞춘다(창 크기·화면 폭·서랍↔열 전환).
  // 열림/닫힘은 의존성이 아니다 — 서랍은 transform 으로만 비켜서므로 **닫혀 있어도 폭이 같다.**
  // 덕분에 열기 전에 이미 배율이 맞아 있고, 여는 순간 다시 재느라 한 번 튀는 일이 없다.
  useEffect(() => {
    const el = boxEl;
    if (!active || el == null || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => {
      fitTriesRef.current = 0; // 폭이 바뀌었으니 줄여 보기를 처음부터 다시 센다
      const next = estimateFit();
      if (next == null) return;
      // 세로 스크롤바가 붙었다 떨어지며 두 값 사이를 오가지 않게 1% 미만 변화는 무시한다
      setFitScale((cur) => (Math.abs(cur - next) < 0.01 ? cur : next));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active, estimateFit, boxEl]);

  // 그린 뒤 한 번 더 재서 넘쳤으면 내린다. 여기서는 **내리기만** 한다 — 올리기까지 하면
  // 두 값 사이를 오간다. 다시 올리는 것은 칸 폭이 바뀌었을 때(위 ResizeObserver)뿐이다.
  useEffect(() => {
    if (!active || zoom != null || previewIds.length === 0) return undefined; // 손으로 배율을 정한 동안은 건드리지 않는다
    let raf = 0;
    let timer = 0;
    let waited = 0;
    const run = () => {
      const el = previewBoxRef.current;
      // 지면은 lazy 로 받아 오고 본문도 뒤늦게 도착한다 — 그려지기 전에 재면 '넘치지 않았다'가
      // 되어 그대로 굳는다. 나타날 때까지 짧게 되물어본다(최대 3초).
      if (el != null && el.querySelector('.pv4-page') == null && waited < 20) {
        waited += 1;
        timer = window.setTimeout(run, 150);
        return;
      }
      shrinkIfOverflow();
    };
    // 배율이 바뀌면 지면이 자체 계산으로 한 번 더 다시 그린다 — 그 뒤에 재도록 한 프레임 미룬다
    raf = window.requestAnimationFrame(run);
    return () => { window.cancelAnimationFrame(raf); window.clearTimeout(timer); };
    // problemPages 가 의존성에 있는 이유: 측정이 비동기라 지면은 나중에 선다. 이 목록에
    // 「지면이 나타났다」를 알릴 값이 없으면 3초 프로브를 한 번 넘긴 뒤 영영 다시 안 돈다.
  }, [active, zoom, fitScale, previewKey, previewIds.length, shrinkIfOverflow, problemPages]);

  // 손 배율의 천장은 100%, 다만 맞춤이 그보다 크면 맞춤까지 — 새 숫자를 만들지 않으면서
  // 넓은 칸에서 [+] 가 지면을 **줄이는** 역전만 막는다(맞춤 125% 에서 +를 누르면 100% 가 됐다).
  const stepZoom = (d) => setZoom(
    Math.min(Math.max(1, fitScale), Math.max(0.1, Math.round((scale + d) * 100) / 100)),
  );
  /** 맞춤 — 가로 스크롤이 나지 않는 가장 큰 배율로 되돌린다.
      누른 그 자리에서 다시 재는 이유: 배율을 손으로 만지는 동안 칸 폭이 바뀌었을 수 있고,
      그사이 지면의 실제 폭도 처음보다 정확해져 있다. */
  const fitToWidth = () => {
    setZoom(null);
    fitTriesRef.current = 0;
    const next = estimateFit();
    if (next != null) setFitScale(next);
  };

  /** 전체화면 [미리보기] — 인쇄·PDF 는 그쪽 몫이다 */
  const openPreview = () => {
    if (problemIds.length === 0) return;
    // ⚠ preset_id 는 넣지 않는다 — 넣으면 레거시 프리셋 인쇄 경로(/exams/presets/print)로 빠진다.
    // 값을 여기서 한 번 굳히는 이유: testData 가 매 렌더 새 객체면 미리보기가 계속 다시 읽는다.
    setPreviewData({
      printContext: context,
      problems_list: problemIds.map((id) => ({ problem_id: Number(id) })),
      preset_name: String(title || '').trim(),
    });
    setPreviewOpen(true);
  };
  const closePreview = () => setPreviewOpen(false);

  const sheetGeo = laid ? laid.geo : geo;
  return {
    previewBoxRef,
    boxRef,
    // 서랍으로 설 때의 폭(px) — 지면 실치수가 들어가는 폭. 부르는 창이 상한(86%)과 함께 쓴다
    drawerPx: Math.ceil(sheetGeo.paper.w * PX_PER_MM) + DRAWER_EXTRA_PX,
    count: problemIds.length,
    laid,
    problemPages,
    answers,
    geo: sheetGeo,
    sheetMeta,
    scale,
    zoom,
    stepZoom,
    fitToWidth,
    missingCount,
    failedCount,
    retryContents,
    previewOpen,
    previewData,
    openPreview,
    closePreview,
    // 머리글 제목을 묵히지 않고 곧바로 — 조립 창이 시험지를 채울 때(열자마자 머리글이 「제목 없는 시험지」로 비지 않게)
    setTitleNow: setPreviewTitle,
  };
}
